import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
import {
  Box,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  useColorModeValue,
  Link,
  VStack,
  HStack,
  Icon,
  Image,
  Progress,
} from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { Spinner as ChakraSpinner } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import UploadImg from "@assets/icons/upload-icon.svg";
import FileIcon from "@assets/icons/file-01.svg";
import CheckIcon from "@assets/icons/CheckIcon.svg";
import CancelIcon from "@assets/icons/CanceIcon.svg";
import { AssessmentsUploadService } from "@root/services/assessments";
import { splitFileIntoChunks, createChunkFormData } from "@root/utils/common";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import {
  ASSESSMENT_TYPE_PARAM,
  PAGINATION_DEFAULTS,
} from "@root/constants/common";

interface UploadRiskAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload?: (file: File) => Promise<void> | void;
  onUploadSuccess?: () => void;
  onStartProcessing?: () => void;
  maxSizeMb?: number;
}

const MAX_DEFAULT_MB = 100;

export const UploadRiskAssessmentModal: React.FC<
  UploadRiskAssessmentModalProps
> = ({
  isOpen,
  onClose,
  onUpload,
  onUploadSuccess,
  onStartProcessing,
  maxSizeMb = MAX_DEFAULT_MB,
}) => {
  const { t } = useTranslation("common");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const progressTimerRef = useRef<any>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const bgPanel = useColorModeValue("white", "gray.800");
  const dropBg = useColorModeValue("#FEF7EE", "gray.700");
  const borderColor = dragOver ? "#DE7F04" : "#DE7F04";
  const baseUrl = process.env.REACT_APP_BASE_URL || "";
  const { fetchRiskAssessments } = useRiskAssessmentContext();

  const isPdf = useCallback(
    (file: File) =>
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf"),
    []
  );
  const isWithinSize = useCallback(
    (file: File) => file.size <= maxSizeMb * 1024 * 1024,
    [maxSizeMb]
  );
  const humanSize = useMemo(() => {
    if (!selectedFile) return "";
    const kb = selectedFile.size / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  }, [selectedFile]);

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const file = files[0];
      if (!isPdf(file)) {
        alert("Only PDF files are supported.");
        return;
      }
      if (!isWithinSize(file)) {
        alert(`File too large. Max ${maxSizeMb}MB allowed.`);
        return;
      }

      setSelectedFile(file);
      setUploadProgress(0);
      setUploading(true);

      // Split into chunks
      const chunks = splitFileIntoChunks(file);
      const uploadService = new AssessmentsUploadService();
      let uploadId: string | undefined;
      let fileKey: string | undefined;

      try {
        for (let i = 0; i < chunks.length; i++) {
          const c = chunks[i];
          const formData = createChunkFormData(c, {
            mimeType: file.type || "application/pdf",
            uploadId,
            fileKey,
          });
          const res: any = await uploadService.uploadChunk(baseUrl, formData);
          // Capture uploadId and fileKey from first response (or any subsequent)
          const data = res?.data?.data || res?.data;
          if (data?.uploadId) uploadId = data.uploadId;
          if (data?.fileKey) fileKey = data.fileKey;

          const progress = Math.round(((i + 1) / chunks.length) * 100);
          setUploadProgress(progress);
        }

        // Refresh uploaded assessments list after successful upload
        await fetchRiskAssessments({
          limit: PAGINATION_DEFAULTS.PAGESIZE,
          offset: 0,
          assessmentType: ASSESSMENT_TYPE_PARAM.UPLOADED,
        });

        // Switch to uploaded tab after successful upload
        if (onUploadSuccess) {
          onUploadSuccess();
        }
      } catch (e) {
        console.error("Chunked upload failed", e);
      } finally {
        setUploading(false);
        // keep file card at 100% so user can click Start Processing
      }
    },
    [
      maxSizeMb,
      baseUrl,
      fetchRiskAssessments,
      onUploadSuccess,
      isPdf,
      isWithinSize,
    ]
  );

  const onDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      await handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const resetState = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
    setUploading(false);
    setSelectedFile(null);
    setUploadProgress(0);
    setDragOver(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleClose = () => {
    if (selectedFile) {
      resetState();
    }
    onClose();
  };

  const onChooseFiles = () => {
    if (uploading) return;
    if (inputRef.current) inputRef.current.value = ""; // ensure onChange fires for same file
    inputRef.current?.click();
  };

  const onRemoveSelected = () => {
    if (uploading) return;
    resetState();
  };

  useEffect(() => {
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent bg={bgPanel} borderRadius="16px" shadow="lg">
        <ModalHeader display="flex" justifyContent="center">
          <Text fontSize="20px" fontWeight="700" color="#1A202C">
            {t("assessments.upload.title")}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6} align="stretch">
            <Box
              border={`2px dashed ${borderColor}`}
              bg={dropBg}
              borderRadius="16px"
              p={8}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <Flex direction="column" align="center" justify="center" gap={3}>
                <Image src={UploadImg} alt="upload-icon" />

                <Text fontSize="14px" color="#6B7280" textAlign="center">
                  {t("assessments.upload.dragDrop")}{" "}
                  <Link
                    color="#005EB8"
                    onClick={onChooseFiles}
                    textDecoration="underline"
                    opacity={uploading ? 0.5 : 1}
                    pointerEvents={uploading ? "none" : "auto"}
                    aria-disabled={uploading}
                  >
                    {t("assessments.upload.chooseFromFiles")}
                  </Link>
                </Text>
                <HStack
                  spacing={2}
                  border="1px solid #005EB8"
                  borderRadius="24px"
                  px={3}
                  py={1}
                  bg="white"
                >
                  <Icon as={InfoIcon} color="#6B7280" />
                  <Text fontSize="12px" color="#6B7280">
                    {t("assessments.upload.supportedType")}{" "}
                    <b>{t("assessments.upload.pdf")}</b> •{" "}
                    {t("assessments.upload.size")} {maxSizeMb} MB
                  </Text>
                </HStack>
              </Flex>
              <input
                ref={inputRef}
                type="file"
                accept="application/pdf,.pdf"
                style={{ display: "none" }}
                onChange={(e) => handleFiles(e.target.files)}
              />
            </Box>
            {selectedFile ? (
              <Box
                border="1px solid #E2E8F0"
                borderRadius="12px"
                p={3}
                bg="white"
              >
                <Flex align="center" justify="space-between" gap={3}>
                  <HStack spacing={3} align="center">
                    <Image src={FileIcon} alt="pdf-icon" />
                    <VStack spacing={0} align="start">
                      <Text fontSize="14px" fontWeight="600" color="#1A202C">
                        {selectedFile?.name}
                      </Text>
                      <HStack spacing={2} align="center">
                        <Text fontSize="12px" color="#6B7280">
                          {humanSize}
                        </Text>
                        <Text fontSize="12px" color="#6B7280">
                          •
                        </Text>
                        {uploadProgress < 100 ? (
                          <HStack spacing={2} align="center">
                            <ChakraSpinner
                              size="xs"
                              color="#6B7280"
                              speed="0.6s"
                              thickness="2px"
                            />
                            <Text fontSize="12px" color="#6B7280">
                              {t("assessments.upload.uploading")}
                            </Text>
                          </HStack>
                        ) : (
                          <Flex align="center">
                            <Image src={CheckIcon} alt="check-icon" />
                            <Text fontSize="12px" ml={1} color="#6B7280">
                              {t("assessments.upload.completed")}
                            </Text>
                          </Flex>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>
                  <Image
                    src={CancelIcon}
                    alt="cancel-icon"
                    marginTop={"-25px"}
                    onClick={onRemoveSelected}
                  />
                </Flex>
                <Box mt={3}>
                  <Progress
                    value={uploadProgress}
                    size="sm"
                    colorScheme="blue"
                    borderRadius="sm"
                  />
                </Box>
              </Box>
            ) : null}

            {selectedFile ? (
              <Flex justify="flex-end" gap={3}>
                <Button
                  height="40px"
                  width={"110px"}
                  border={"1px solid #CDD6DC"}
                  bg={"white"}
                  color={"#5C6670"}
                  borderRadius="8px"
                  onClick={handleClose}
                  isDisabled={uploading}
                >
                  {t("assessments.upload.cancel")}
                </Button>
                <Button
                  height="40px"
                  width={"180px"}
                  bg={"#005EB8"}
                  color={"white"}
                  borderRadius="8px"
                  disabled={!selectedFile || uploadProgress < 100}
                  isDisabled={!selectedFile || uploadProgress < 100}
                  onClick={() => {
                    if (onStartProcessing) {
                      onStartProcessing();
                    }
                    resetState();
                    onClose();
                  }}
                >
                  {t("assessments.upload.startProcessing")}
                </Button>
              </Flex>
            ) : null}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default UploadRiskAssessmentModal;
